import { useEffect, useState } from 'react';
import api from '../../services/api';
import type { WatchEvent, WatchRunSearches } from '../../types';
import SerpModal from '../SerpModal';
import EventDetails from './EventDetails';
import { competitorQueries, positionWords, searchRows, visitsWords, volumeWords, type SearchRow } from './searchRows';
import { when } from './watchEventLines';

function Row({ row, onOpen }: { row: SearchRow; onOpen: (row: SearchRow) => void }) {
  const [open, setOpen] = useState(false);
  const what = row.events.map(e => e.title).join(' · ');
  return (
    <>
      <tr className="border-t border-gray-100 dark:border-gray-700">
        <td className="py-1 pr-3">
          <button
            type="button"
            onClick={() => onOpen(row)}
            className="text-left text-indigo-600 hover:underline dark:text-indigo-300"
            title="See the search results this check read"
          >
            {row.query}
          </button>
        </td>
        <td className="py-1 pr-3 tabular-nums text-gray-600 dark:text-gray-300">
          {positionWords(row.was, row.compared)}
        </td>
        <td className="py-1 pr-3 tabular-nums text-gray-900 dark:text-gray-100">{positionWords(row.now)}</td>
        <td className="py-1 pr-3 tabular-nums text-gray-600 dark:text-gray-300">{volumeWords(row.monthly_searches)}</td>
        <td
          className={`py-1 pr-3 tabular-nums ${
            row.visits_was !== null && row.visits_now !== null && row.visits_now < row.visits_was
              ? 'font-medium text-red-700 dark:text-red-300'
              : 'text-gray-600 dark:text-gray-300'
          }`}
        >
          {visitsWords(row.visits_was, row.visits_now)}
        </td>
        <td className="py-1">
          {row.events.length ? (
            <button
              type="button"
              onClick={() => setOpen(o => !o)}
              aria-expanded={open}
              className="text-left text-gray-800 hover:underline dark:text-gray-100"
            >
              {what} {open ? '▾' : '▸'}
            </button>
          ) : (
            <span className="text-gray-500 dark:text-gray-400">
              {!row.compared
                ? 'first look'
                : !row.changed
                  ? 'no change'
                  : row.monthly_searches !== null && row.monthly_searches < 10
                    ? 'changed - no alert, under 10 searches a month'
                    : 'changed, too little to report'}
            </span>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} className="pb-2">
            {row.events.map(e => (
              <div key={e.id}>
                {e.so_what && <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">{e.so_what}</p>}
                <EventDetails event={e} />
              </div>
            ))}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * WHAT ONE RANK CHECK READ, AS SEARCHES. Opening a rank check showed a stack
 * of alert boxes, each repeating its tier. This is one table, a row per
 * search: where they were, where they are now and what changed, each search
 * opening the results it was read from. Domains new across the set go below.
 */
export default function SearchChanges({ runId, events = [] }: { runId: number; events?: WatchEvent[] }) {
  const [data, setData] = useState<WatchRunSearches | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const [serp, setSerp] = useState<SearchRow | null>(null);
  useEffect(() => {
    let alive = true;
    api
      .getWatchRunSearches(runId)
      .then(d => alive && setData(d))
      .catch(e => alive && setError(e instanceof Error ? e.message : 'Could not load the searches'));
    return () => {
      alive = false;
    };
  }, [runId]);
  if (error) return <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>;
  if (!data) return <p className="mt-2 text-xs text-gray-400">Loading the searches…</p>;
  const { rows, acrossSet } = searchRows(data, events);
  const loud = rows.filter(r => r.events.length || r.changed);
  // Only what changed, unless nothing did.
  const shown = all || !loud.length ? rows : loud;
  return (
    <div className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-700" data-testid={`search-changes-${runId}`}>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {data.engine ? `Results from ${data.engine}.` : ''}
        {data.compared_at ? ` Compared with the check of ${when(data.compared_at)}.` : ''}
      </p>
      <table className="mt-1 w-full text-xs">
        <thead>
          <tr className="text-left text-gray-500 dark:text-gray-400">
            <th className="py-1 pr-3 font-medium">Search</th>
            <th className="py-1 pr-3 font-medium">Before</th>
            <th className="py-1 pr-3 font-medium">Now</th>
            <th className="py-1 pr-3 font-medium" title="How many times a month people make this search">
              Searches a month
            </th>
            <th
              className="py-1 pr-3 font-medium"
              title="Estimated visits a month from this search at that position, from typical click shares"
            >
              Visits a month (est.)
            </th>
            <th className="py-1 font-medium">What changed</th>
          </tr>
        </thead>
        <tbody>
          {shown.map(r => (
            <Row key={r.keyword_id} row={r} onOpen={setSerp} />
          ))}
        </tbody>
      </table>
      {rows.length > shown.length || all ? (
        <button
          type="button"
          onClick={() => setAll(a => !a)}
          className="mt-1 text-xs text-indigo-600 hover:underline dark:text-indigo-300"
        >
          {all ? 'Show only what changed' : `Show all ${rows.length} searches`}
        </button>
      ) : null}
      {acrossSet.length > 0 && (
        <div className="mt-2">
          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-200">Across these searches</h4>
          <ul className="mt-0.5 flex flex-col gap-0.5 text-xs text-gray-700 dark:text-gray-200">
            {acrossSet.map(e => {
              const queries = competitorQueries(e, rows);
              const domain = String(e.payload?.detail ?? '');
              // Said here from the check's results: an older alert's sentence lacks the count.
              if (e.kind !== 'new_competitor_in_set' || !domain || !queries.length) {
                return <li key={e.id}>{e.so_what || e.headline}</li>;
              }
              return (
                <li key={e.id}>
                  <span className="font-medium">{domain}</span> now shows up in {queries.length} of these searches. It
                  is not on your watch list.
                  <span className="text-gray-500 dark:text-gray-400"> ({queries.join(', ')})</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {serp && <SerpModal isOpen onClose={() => setSerp(null)} keywordId={serp.keyword_id} keywordValue={serp.query} />}
    </div>
  );
}
